import React, { useRef } from 'react';
import { useTheme } from './ThemeProvider';

/**
 * Uniform File Upload Component
 * 
 * Features:
 * - Clean "Browse..." button design
 * - Inline file status display
 * - Image preview with remove option
 * - Theme-aware styling
 * - Accessible HTML structure
 * 
 * Usage:
 * <FileUploadField
 *   label="Product Image"
 *   accept="image/*"
 *   value={selectedFile}
 *   preview={previewUrl}
 *   onChange={handleFileChange}
 *   onRemove={handleRemove}
 *   optional={true}
 * />
 */
export default function FileUploadField({
    label,
    accept = "image/*",
    value,
    preview,
    onChange,
    onRemove,
    optional = false,
    disabled = false,
    error = null,
    helperText = null
}) {
    const fileInputRef = useRef(null);
    const { currentTheme } = useTheme();

    const handleBrowseClick = () => {
        if (!disabled && fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileInput = (e) => {
        const file = e.target.files?.[0];
        if (file && onChange) {
            onChange(file);
        }
    };

    const getFileStatus = () => {
        if (value && value.name) {
            return value.name;
        }
        return "No file selected.";
    };

    return (
        <div className="file-upload-field">
            {/* Label */}
            {label && (
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    {label}
                    {optional && <span className="text-gray-500 text-xs ml-1">(optional)</span>}
                </label>
            )}

            {/* Upload Area */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-300">
                {/* Browse Button */}
                <button
                    type="button"
                    onClick={handleBrowseClick}
                    disabled={disabled}
                    className="px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition"
                    style={{
                        backgroundColor: currentTheme?.color || '#1e40af',
                        color: '#fff',
                        opacity: disabled ? 0.6 : 1,
                        cursor: disabled ? 'not-allowed' : 'pointer',
                    }}
                    onMouseOver={(e) => !disabled && (e.currentTarget.style.opacity = '0.9')}
                    onMouseOut={(e) => !disabled && (e.currentTarget.style.opacity = '1')}
                >
                    Browse...
                </button>

                {/* Hidden File Input */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={accept}
                    onChange={handleFileInput}
                    disabled={disabled}
                    className="hidden"
                    aria-label={label || "File upload"}
                />

                {/* File Status */}
                <span className="text-gray-700 text-sm flex-1">
                    {getFileStatus()}
                </span>
            </div>

            {/* Error Message */}
            {error && (
                <p className="mt-1 text-xs text-red-600">{error}</p>
            )}

            {/* Helper Text */}
            {helperText && !error && (
                <p className="mt-1 text-xs text-gray-500">{helperText}</p>
            )}

            {/* Preview (if image selected) */}
            {preview && (
                <div className="mt-3 flex items-center gap-3">
                    <img
                        src={preview}
                        alt="File preview"
                        className="w-16 h-16 object-contain border rounded-md bg-white"
                    />
                    <button
                        type="button"
                        onClick={onRemove}
                        disabled={disabled}
                        className="px-3 py-1 text-xs rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Remove
                    </button>
                </div>
            )}
        </div>
    );
}
